import axios from 'axios';
import { BQRegistration, BQRegistrationData, BQPlayerData, BQApprovalData, BQRejectData, BQCancelApprovalData } from '../../types/bq';

// 개발/프로덕션 모두 상대 경로 사용 → 개발 서버 setupProxy가 /api/bq 를 백엔드로 전달 (3000/3006 등 어떤 포트에서도 동일 동작)
const BASE_URL = '';

const bqAxios = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// API 경로 상수
const API_PATHS = {
  REGISTER: '/api/bq/register',
  REGISTER_CHECK: '/api/bq/register/check',
  PARTICIPANT: '/api/bq/participant',
  ADMIN_REGISTRATION_DATA: '/api/bq/admin/events',
  ADMIN_PLAYER_DATA: '/api/bq/admin/events',
  ADMIN_APPROVE: '/api/bq/admin/approve',
  ADMIN_REJECT: '/api/bq/admin/reject',
  ADMIN_CANCEL_APPROVAL: '/api/bq/admin/cancel-approval'
};

export interface ChurchCheckParams {
  churchNumber: string;
  pin: string;
}

export interface ChurchCheckResponse {
  success: boolean;
  data?: {
    mainId: string;
    subId: string;
    name: string;
    managerName?: string;
    phone?: string;
  };
  error?: string;
}

export const bqApi = {
  // 성경퀴즈 참가 신청
  register: async (eventId: string, churchId: string, registration: BQRegistration): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await bqAxios.post<{ success: boolean; message?: string }>(
        `${API_PATHS.REGISTER}`,
        { eventId, churchId, ...registration }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error registering BQ:', error);
      throw new Error(error.response?.data?.error || error.message || '등록에 실패했습니다.');
    }
  },

  // 기존 등록 데이터 조회
  getRegistration: async (eventId: string, churchId: string): Promise<BQRegistrationData> => {
    try {
      const response = await bqAxios.get<BQRegistrationData>(
        `${API_PATHS.REGISTER}/${eventId}/${churchId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching BQ registration:', error);
      throw new Error(error.response?.data?.error || error.message || '등록 데이터를 불러오는데 실패했습니다.');
    }
  },

  // 교회 등록 확인
  checkChurch: async (params: ChurchCheckParams): Promise<ChurchCheckResponse> => {
    try {
      const response = await bqAxios.post<ChurchCheckResponse>(
        API_PATHS.REGISTER_CHECK,
        params
      );
      return response.data;
    } catch (error: any) {
      console.error('Error checking church:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || '교회 확인에 실패했습니다.'
      };
    }
  },

  // 참가자 수정
  updateParticipant: async (
    eventId: string,
    participantId: number | string,
    data: { handbook?: string; playerName?: string; coachName?: string; coachContact?: string }
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await bqAxios.put<{ success: boolean; message?: string }>(
        `${API_PATHS.PARTICIPANT}/${eventId}/${participantId}`,
        data
      );
      return response.data;
    } catch (error: any) {
      console.error('Error updating participant:', error);
      throw new Error(error.response?.data?.error || error.message || '참가자 수정에 실패했습니다.');
    }
  },

  // 참가자 삭제
  deleteParticipant: async (eventId: string, participantId: number | string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await bqAxios.delete<{ success: boolean; message?: string }>(
        `${API_PATHS.PARTICIPANT}/${eventId}/${participantId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error deleting participant:', error);
      throw new Error(error.response?.data?.error || error.message || '참가자 삭제에 실패했습니다.');
    }
  },

  // 등록 데이터 조회 (관리자) - 성취 기록 카드와 동일하게 eventId 사용
  getRegistrationData: async (eventId: string): Promise<any[]> => {
    try {
      const response = await bqAxios.get<any[]>(
        `${API_PATHS.ADMIN_REGISTRATION_DATA}/${eventId}/registration-data`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching registration data:', error);
      throw new Error(error.response?.data?.error || error.response?.data?.message || error.message || '등록 데이터를 불러오는데 실패했습니다.');
    }
  },

  // 선수 데이터 조회 (관리자)
  getPlayerData: async (eventId: string): Promise<BQPlayerData[]> => {
    try {
      const response = await bqAxios.get<BQPlayerData[]>(
        `${API_PATHS.ADMIN_PLAYER_DATA}/${eventId}/player-data`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching player data:', error);
      throw new Error(error.response?.data?.error || error.message || '선수 데이터를 불러오는데 실패했습니다.');
    }
  },

  // 사진 승인 및 영수증 자동 발행
  approve: async (approvalData: BQApprovalData): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await bqAxios.post<{ success: boolean; message?: string }>(
        API_PATHS.ADMIN_APPROVE,
        approvalData
      );
      return response.data;
    } catch (error: any) {
      console.error('Error approving:', error);
      throw new Error(error.response?.data?.error || error.message || '승인 처리에 실패했습니다.');
    }
  },

  // 사진 반려 및 영수증 삭제
  reject: async (rejectData: BQRejectData): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await bqAxios.post<{ success: boolean; message?: string }>(
        API_PATHS.ADMIN_REJECT,
        rejectData
      );
      return response.data;
    } catch (error: any) {
      console.error('Error rejecting:', error);
      throw new Error(error.response?.data?.error || error.message || '반려 처리에 실패했습니다.');
    }
  },

  // 승인 취소
  cancelApproval: async (cancelData: BQCancelApprovalData): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await bqAxios.post<{ success: boolean; message?: string }>(
        API_PATHS.ADMIN_CANCEL_APPROVAL,
        cancelData
      );
      return response.data;
    } catch (error: any) {
      console.error('Error cancelling approval:', error);
      throw new Error(error.response?.data?.error || error.message || '승인 취소에 실패했습니다.');
    }
  },

  // 사진 업로드
  uploadPhoto: async (
    eventId: string,
    type: string,
    participantId: number | string,
    file: File,
    playerName: string,
    churchName: string,
    region: string
  ): Promise<{ success: boolean; message?: string; photo_path?: string }> => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('eventId', eventId);
      formData.append('type', type);
      formData.append('participantId', participantId.toString());
      formData.append('playerName', playerName);
      formData.append('churchName', churchName);
      formData.append('region', region);

      const response = await bqAxios.post<{ success: boolean; message?: string; photo_path?: string }>(
        '/api/bq/upload-photo',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      throw new Error(error.response?.data?.error || error.message || '사진 업로드에 실패했습니다.');
    }
  },

  // 업로드된 사진 조회
  getPhotoUrl: (event: string, photoPath: string): string => {
    if (!photoPath) return '';
    const isProduction = process.env.NODE_ENV === 'production';
    if (isProduction) {
      // 프로덕션: bq-data/images/ 경로 사용
      return `https://awanaevent.com/${photoPath}`;
    } else {
      // 개발: bq-service에서 직접 제공 (정적 파일 서빙)
      return `http://localhost:3005/${photoPath}`;
    }
  },

  // 영수증 데이터 내보내기 (상대 경로 사용 시 같은 오리진 → 프록시 필요 시 setupProxy에 /admin 추가)
  exportReceipts: async (eventYear: string, eventName: string): Promise<any[]> => {
    try {
      const uploadBaseUrl = ''; // 상대 경로

      const response = await fetch(`${uploadBaseUrl}/admin/receipts/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_year: eventYear,
          event_name: eventName,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Error exporting receipts:', error);
      throw new Error(error.message || '영수증 데이터 내보내기에 실패했습니다.');
    }
  },

  // 영수증 동기화
  syncReceipts: async (eventId: string): Promise<{ success: boolean; message?: string; synced?: number; created?: number; updated?: number; errors?: string[] }> => {
    try {
      const response = await bqAxios.post<{ success: boolean; message?: string; synced?: number; created?: number; updated?: number; errors?: string[] }>(
        `${API_PATHS.ADMIN_REGISTRATION_DATA}/${eventId}/sync-receipts`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error syncing receipts:', error);
      throw new Error(error.response?.data?.error || error.message || '영수증 동기화에 실패했습니다.');
    }
  },

  // 접수내역 통계 조회
  getRegistrationSummary: async (eventId: string): Promise<any[]> => {
    try {
      const response = await bqAxios.get<any[]>(
        `${API_PATHS.ADMIN_REGISTRATION_DATA}/${eventId}/registration-summary`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching registration summary:', error);
      throw new Error(error.response?.data?.error || error.message || '접수내역 통계를 불러오는데 실패했습니다.');
    }
  },

  // 등록 총 금액 수정 (관리자)
  updateTotalCosts: async (eventId: string, churchId: string, totalcosts: number): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await bqAxios.put<{ success: boolean; message?: string }>(
        `/api/bq/admin/registrations/${eventId}/${churchId}/totalcosts`,
        { totalcosts }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error updating total costs:', error);
      throw new Error(error.response?.data?.error || error.message || '총 금액 수정에 실패했습니다.');
    }
  },

  // 등록 삭제 (관리자)
  deleteRegistration: async (eventId: string, churchId: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await bqAxios.delete<{ success: boolean; message?: string }>(
        `/api/bq/admin/registrations/${eventId}/${churchId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error deleting registration:', error);
      throw new Error(error.response?.data?.error || error.message || '등록 삭제에 실패했습니다.');
    }
  }
};

